import { ElementRef, useEffect, useRef, useState } from 'react'
import { Popover, Transition } from '@headlessui/react'

import { ToolsMenuContainer, ToolsMenuTooltip } from './styles'

import ModalAnnotation from 'components/ModalAnnotation'
import RangerSlider from 'components/RangerSlider'

import {
  EventNoteIcon,
  FontIncreaseIcon,
  MarkerIcon,
  QuestionCircleIcon,
  ScreenEnlargeIcon,
  SolidToolsIcon,
  CrossIcon,
} from './icons'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store/modules/rootReducer'
import { setInfoViewStatus } from 'store/modules/showToolsMenuInfo/action'
import { useGetExcertoAnnotations } from 'pages/Lesson/hooks/use-get-excerto-annotations'
import { useLessonParams } from 'pages/Lesson/hooks/use-lesson-params'

interface ToolsMenuProps {
  fontSize?: number
  setFontSize(fontSize: number): void
  name: string
  objectId: string
  setColor(string): void
  isFreemium: boolean
  setOpenModalFreemium(boolean): void
}

type TAnnotationType = 'marks' | 'notes'

const ToolsMenu: React.FC<ToolsMenuProps> = ({
  fontSize = 1,
  setFontSize,
  name,
  objectId,
  setColor,
  isFreemium,
  setOpenModalFreemium,
}) => {
  const { lessonId, moduleId, chapterId, courseId } = useLessonParams()
  const dispatch = useDispatch()

  const { infoViewed } = useSelector((state: RootState) => state.toolsMenuInfo)

  const [openToolsMenu, setOpenToolsMenu] = useState(infoViewed ? false : true)
  const [openRangerSlider, setRangerSlider] = useState(false)
  const [openModalByType, setOpenModalByType] = useState<TAnnotationType | false>(false)
  const [initialTimeoutId, setInitialTimeoutId] = useState(null)

  const rangeRef = useRef<ElementRef<'div'>>()
  const toolsMenuRef = useRef<ElementRef<'div'>>()

  const swrAnnotations = useGetExcertoAnnotations({ lessonId, moduleId, chapterId, courseId })

  let touchStartPos = null

  function toggleFullScreen() {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        return document.documentElement.requestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        return document.exitFullscreen()
      }
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (
        !rangeRef.current ||
        rangeRef.current.contains(event.target) ||
        event.target?.id === 'increaseFontButton'
      ) {
        return
      }

      setRangerSlider(false)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [rangeRef])

  useEffect(() => {
    const listener = (event) => {
      if (!toolsMenuRef.current || toolsMenuRef.current.contains(event.target) || !infoViewed) {
        return
      }

      if (initialTimeoutId) {
        clearTimeout(initialTimeoutId)
        setInitialTimeoutId(null)
      }

      setOpenToolsMenu(false)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [toolsMenuRef, infoViewed, initialTimeoutId])

  return (
    <ToolsMenuContainer
      onTouchStart={(e) => (touchStartPos = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        const touchEndPos = e.changedTouches[0].clientX

        if (!touchStartPos) {
          return
        }

        if (touchStartPos - touchEndPos > 10) {
          setOpenToolsMenu(true)
        }
      }}
    >
      {openRangerSlider ? (
        <RangerSlider
          close={() => setRangerSlider(false)}
          fontSize={fontSize}
          setFontSize={setFontSize}
          ref={rangeRef}
        />
      ) : null}

      {openModalByType && (
        <ModalAnnotation
          name={name}
          typeModal={openModalByType}
          refetchAnnotations={swrAnnotations.mutate}
          close={() => setOpenModalByType(false)}
          lessonId={lessonId}
          moduleId={moduleId}
          chapterId={chapterId}
          courseId={courseId}
          objectId={objectId}
          setColor={setColor}
          isFreemium={isFreemium}
          annotations={swrAnnotations.data[openModalByType]}
          loadingAnotation={swrAnnotations.isValidating}
          setOpenModalFreemium={setOpenModalFreemium}
        />
      )}
      <Popover.Group className="popoverGroup">
        <Popover className="infoPopoverContainer">
          {!infoViewed && (
            <>
              <Popover.Overlay className="infoPopoverOverlay" static />
              <Popover.Panel static className="infoPopoverPanel">
                <button
                  className="closeButton"
                  onClick={() => {
                    dispatch(setInfoViewStatus(true))
                    const id = setTimeout(() => {
                      setOpenToolsMenu(false)
                    }, 5000)
                    setInitialTimeoutId(id)
                  }}
                >
                  <CrossIcon />
                </button>
                <div className="helpImage"></div>
                <div className="infoText">
                  <div className="mainText">
                    <p>Seu menu de ferramentas mudou!</p>
                    <button>
                      <CrossIcon />
                    </button>
                  </div>
                  <p>
                    Agora seu acesso às marcações, anotações e demais ferramentas encontra-se na
                    lateral.
                  </p>
                </div>
                <button
                  className="finishButton"
                  onClick={() => {
                    dispatch(setInfoViewStatus(true))
                    const id = setTimeout(() => {
                      setOpenToolsMenu(false)
                    }, 5000)
                    setInitialTimeoutId(id)
                  }}
                >
                  Concluir
                </button>
              </Popover.Panel>
            </>
          )}
        </Popover>

        <Popover className="popoverContainer" ref={toolsMenuRef}>
          <>
            <Popover.Button
              id="configs"
              className={`popoverButton transition ${openToolsMenu ? 'open' : 'close'}`}
              onClick={() => {
                setOpenToolsMenu(!openToolsMenu)
                if (initialTimeoutId) {
                  clearTimeout(initialTimeoutId)
                  setInitialTimeoutId(null)
                }
              }}
              disabled={infoViewed ? false : true}
            >
              <SolidToolsIcon />
            </Popover.Button>

            <Transition
              show={openToolsMenu}
              enter="transition"
              enterFrom="enterFrom"
              enterTo="enterTo"
              leave="transition"
              leaveFrom="leaveFrom"
              leaveTo="leaveTo"
              static
            >
              {openToolsMenu && (
                <Popover.Panel className="popoverPanel" static>
                  <div className="actionsWrapper">
                    <ToolsMenuTooltip title="Tela cheia">
                      <button
                        id="fullScreen"
                        onClick={toggleFullScreen}
                        disabled={infoViewed ? false : true}
                      >
                        <ScreenEnlargeIcon />
                      </button>
                    </ToolsMenuTooltip>
                    <ToolsMenuTooltip title="Anotações">
                      <button
                        id="notes"
                        onClick={() => setOpenModalByType('notes')}
                        disabled={infoViewed ? false : true}
                      >
                        <EventNoteIcon />
                      </button>
                    </ToolsMenuTooltip>
                    <ToolsMenuTooltip title="Marcações">
                      <button
                        id="annotations"
                        onClick={() => setOpenModalByType('marks')}
                        disabled={infoViewed ? false : true}
                      >
                        <MarkerIcon />
                      </button>
                    </ToolsMenuTooltip>
                    <ToolsMenuTooltip title="Tamanho da fonte">
                      <button
                        id="slider"
                        onClick={() => setRangerSlider(!openRangerSlider)}
                        disabled={infoViewed ? false : true}
                      >
                        <FontIncreaseIcon id="increaseFontButton" />
                      </button>
                    </ToolsMenuTooltip>
                    <ToolsMenuTooltip title="Dúvidas">
                      <button id="doubts">
                        <a href={infoViewed ? '/duvidas' : ''}>
                          <QuestionCircleIcon />
                        </a>
                      </button>
                    </ToolsMenuTooltip>
                  </div>
                </Popover.Panel>
              )}
            </Transition>
          </>
        </Popover>
      </Popover.Group>
    </ToolsMenuContainer>
  )
}

export default ToolsMenu
